import type {ComponentAction, ConversionDal, StructureIterators} from '../../types'
import {createImmutableProxy} from '@wix/wix-immutable-proxy'
import type {Pointer} from '@wix/document-services-types'
import type {StructureEntryItem} from '../types'
import {pointerUtils} from '@wix/document-manager-core'
import {ChildrenProperties} from '../../enums'

const defaultChildrenProperties: ChildrenProperties[] = [ChildrenProperties.components]

export const createStructureIterators = (
    conversionDal: ConversionDal,
    rootPointer: Pointer
): StructureIterators<any> => {
    const getStructureById = (id: string) => conversionDal.get(pointerUtils.getPointerFromPointer(id, rootPointer))

    const getChildrenToIterate = (comp: StructureEntryItem, childrenProperties: ChildrenProperties[]): string[] =>
        childrenProperties.reduce(
            (children: string[], childrenProperty: ChildrenProperties) => [
                ...children,
                ...(comp[childrenProperty] || [])
            ],
            []
        )

    function* flatStructureIterator(
        comp: StructureEntryItem,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ): Generator<StructureEntryItem> {
        yield comp

        const childrenToIterate = getChildrenToIterate(comp, childrenProperties)

        for (const childId of childrenToIterate) {
            yield* flatStructureIterator(getStructureById(childId))
        }
    }

    function* postOrderFlatStructureIterator(
        comp: StructureEntryItem,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ): Generator<StructureEntryItem> {
        const childrenToIterate = getChildrenToIterate(comp, childrenProperties)

        for (const childId of childrenToIterate) {
            yield* postOrderFlatStructureIterator(getStructureById(childId))
        }

        yield comp
    }

    const forEachComponent = (
        action: ComponentAction<any>,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ) => {
        for (const component of flatStructureIterator(conversionDal.get(rootPointer), childrenProperties)) {
            action(createImmutableProxy(component), pointerUtils.getPointerFromPointer(component.id, rootPointer))
        }
    }

    const forEachComponentAsync = async (
        action: ComponentAction<any>,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ) => {
        for (const component of flatStructureIterator(conversionDal.get(rootPointer), childrenProperties)) {
            await action(createImmutableProxy(component), pointerUtils.getPointerFromPointer(component.id, rootPointer))
        }
    }

    const forEachComponentReverse = (
        action: ComponentAction<any>,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ) => {
        for (const component of Array.from(
            flatStructureIterator(conversionDal.get(rootPointer), childrenProperties)
        ).reverse()) {
            action(createImmutableProxy(component), pointerUtils.getPointerFromPointer(component.id, rootPointer))
        }
    }

    const forEachComponentPostOrder = (
        action: ComponentAction<any>,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ) => {
        for (const component of postOrderFlatStructureIterator(conversionDal.get(rootPointer), childrenProperties)) {
            action(createImmutableProxy(component), pointerUtils.getPointerFromPointer(component.id, rootPointer))
        }
    }

    const forEachComponentPostOrderAsync = async (
        action: ComponentAction<any>,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ) => {
        for (const component of postOrderFlatStructureIterator(conversionDal.get(rootPointer), childrenProperties)) {
            await action(createImmutableProxy(component), pointerUtils.getPointerFromPointer(component.id, rootPointer))
        }
    }

    return {
        getRootPointer: () => rootPointer,
        forEachComponent,
        forEachComponentAsync,
        forEachComponentReverse,
        forEachComponentPostOrder,
        forEachComponentPostOrderAsync
    }
}
